import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../common/Button";
import Input from "../common/Input";
import { projectService } from "../../services/projects";
import { clientService } from "../../services/clients";
import { teamService } from "../../services/team";
import toast from "react-hot-toast";

const ProjectForm = ({ project, onSuccess }) => {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: project || {},
  });
  const [loading, setLoading] = useState(false);
  const [deliverables, setDeliverables] = useState([]);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);

  const { data: clients } = useQuery({
    queryKey: ["clients"],
    queryFn: clientService.getAll,
  });

  const { data: teamMembers } = useQuery({
    queryKey: ["team"],
    queryFn: teamService.getAll,
  });

  useEffect(() => {
    if (project) {
      reset({
        name: project.name || "",
        client: project.client?._id || project.client || "",
        status: project.status || "in_progress",
        progress: project.progress || 0,
      });
      setDeliverables(
        (project.deliverables || []).map((d) => ({
          name: d.name || "",
          deadline: d.deadline?.split("T")[0] || "",
          assignedTo: d.assignedTo?._id || d.assignedTo || "",
          completed: d.completed || false,
        }))
      );
      setSelectedTeamMembers(
        (project.teamMembers || []).map((m) => m._id || m)
      );
    } else {
      reset({});
      setDeliverables([]);
      setSelectedTeamMembers([]);
    }
  }, [project, reset]);

  const handleTeamMemberToggle = (memberId) => {
    setSelectedTeamMembers((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  const addDeliverable = () => {
    setDeliverables((prev) => [
      ...prev,
      { name: "", deadline: "", assignedTo: "", completed: false },
    ]);
  };

  const removeDeliverable = (index) => {
    setDeliverables((prev) => prev.filter((_, i) => i !== index));
  };

  const updateDeliverable = (index, field, value) => {
    setDeliverables((prev) =>
      prev.map((d, i) => (i === index ? { ...d, [field]: value } : d))
    );
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const payload = {
      ...data,
      progress: Number(data.progress) || 0,
      teamMembers: selectedTeamMembers,
      deliverables: deliverables.filter((d) => d.name.trim()),
    };
    try {
      if (project) {
        await projectService.update(project._id, payload);
        toast.success("Project updated");
      } else {
        await projectService.create(payload);
        toast.success("Project created");
      }
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      reset();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Name"
        {...register("name", { required: "Name is required" })}
        error={errors.name?.message}
      />
      <div>
        <label className="block text-sm font-medium mb-1">Client</label>
        <select
          {...register("client", { required: "Client is required" })}
          className="w-full border rounded p-2"
        >
          <option value="">Select Client</option>
          {(clients || []).map((c) => (
            <option key={c._id} value={c._id}>
              {c.name}
            </option>
          ))}
        </select>
        {errors.client && (
          <p className="mt-1 text-sm text-red-500">{errors.client.message}</p>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Status</label>
        <select
          {...register("status")}
          className="w-full border rounded p-2"
        >
          <option value="in_progress">In Progress</option>
          <option value="review">Review</option>
          <option value="completed">Completed</option>
          <option value="on_hold">On Hold</option>
        </select>
      </div>
      <Input
        label="Progress (0-100)"
        type="number"
        {...register("progress", { min: 0, max: 100 })}
      />
      <div>
        <label className="block text-sm font-medium mb-1">Team Members</label>
        <div className="border rounded p-2 max-h-40 overflow-y-auto space-y-1">
          {(teamMembers || []).length === 0 && (
            <p className="text-sm text-gray-500">No team members available</p>
          )}
          {(teamMembers || []).map((member) => (
            <label key={member._id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selectedTeamMembers.includes(member._id)}
                onChange={() => handleTeamMemberToggle(member._id)}
              />
              {member.name}
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium">Deliverables</label>
          <Button type="button" variant="secondary" onClick={addDeliverable}>
            + Add Deliverable
          </Button>
        </div>
        {deliverables.map((deliverable, index) => (
          <div
            key={index}
            className="border rounded p-3 mb-2 space-y-2 bg-gray-50"
          >
            <div className="flex justify-between items-start">
              <span className="text-sm font-medium text-gray-600">
                #{index + 1}
              </span>
              <Button
                type="button"
                variant="danger"
                className="text-xs px-2 py-1"
                onClick={() => removeDeliverable(index)}
              >
                Remove
              </Button>
            </div>
            <input
              type="text"
              placeholder="Deliverable name"
              value={deliverable.name}
              onChange={(e) =>
                updateDeliverable(index, "name", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Deadline
                </label>
                <input
                  type="date"
                  value={deliverable.deadline}
                  onChange={(e) =>
                    updateDeliverable(index, "deadline", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Assigned To
                </label>
                <select
                  value={deliverable.assignedTo}
                  onChange={(e) =>
                    updateDeliverable(index, "assignedTo", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Member</option>
                  {(teamMembers || []).map((member) => (
                    <option key={member._id} value={member._id}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={deliverable.completed}
                onChange={(e) =>
                  updateDeliverable(index, "completed", e.target.checked)
                }
              />
              Completed
            </label>
          </div>
        ))}
      </div>
      <Button type="submit" disabled={loading}>
        {loading ? "Saving..." : project ? "Update" : "Create"}
      </Button>
    </form>
  );
};

export default ProjectForm;
